import * as angular from "angular";
import { ElectionDataProvider } from "../../data-provider/election.dp";
import { FieldDataProvider } from "../../data-provider/field.dp";
import { VoterFileDataProvider } from "../../data-provider/voter-file.dp";
import { Field } from "../../models/field.model";
import { DialogUtils } from "../../helper/dialog.helper";
import { ToastUtils } from "../../helper/toast.helper";

let FieldPickerComponent = {
    selector: "vcFieldPicker",
    templateUrl: "/angular/components/voter-file/field-picker.component.html",
    bindings: {
        column: "=",
        registerColumns: "=",
        defaultColumn: "=",
        importDataType: "=",
    },
    controller: class FieldPickerController {
        // * bindings for typescript compiler *
        public column: any;
        public registerColumns: any;
        public defaultColumn: any;
        public importDataType: string;
        // ************************************

        private readonly $scope: any;
        private dialog: DialogUtils;
        private dataProvider: VoterFileDataProvider;
        private electionDataProvider: ElectionDataProvider;
        private fieldDataProvider: FieldDataProvider;
        private readonly toast: ToastUtils;

        public columnname: string | null = null;
        public isLeaderView: boolean;
        public product: string;
        public elections: object[];
        public customFields: Field[];
        public fieldCategories: Field[];

        constructor(
            $scope: any,
            DialogUtils: DialogUtils,
            VoterFileDataProvider: VoterFileDataProvider,
            ElectionDataProvider: ElectionDataProvider,
            FieldDataProvider: FieldDataProvider,
            ToastUtils: ToastUtils,
            __app_type: string,
            __env: any
        ) {
            this.$scope = $scope;
            this.dialog = DialogUtils;
            this.dataProvider = VoterFileDataProvider;
            this.electionDataProvider = ElectionDataProvider;
            this.fieldDataProvider = FieldDataProvider;
            this.toast = ToastUtils;

            this.isLeaderView = __app_type == "LEADER";
            this.product = __env.product;
            this.electionDataProvider
                .getAllElections()
                .then((elections: object[]) => {
                    this.elections = elections;
                });

            this.fieldDataProvider
                .getCustomFields()
                .then((customFields: Field[]) => {
                    this.customFields = customFields;
                });
        }

        $onInit() {
            if (this.column.data.length && this.column.data[0]) {
                this.columnname = this.column.data[0];
            }

            this.dataProvider.getFields().then((categoryFields: Field[]) => {
                let fieldsWithHoKey = this.getFieldsHelper(categoryFields);

                this.fieldDataProvider
                    .getAllFields()
                    .then((fields: Field[]) => {
                        angular.forEach(fieldsWithHoKey, (field: Field) => {
                            field.columnname =
                                this.fieldDataProvider.matchDynamicFieldName(
                                    fields,
                                    field
                                ) || this.column.name;
                        });
                    });

                this.fieldCategories = categoryFields;
            });
        }

        getFieldsHelper(categoryFields: Field[]): Field[] {
            let allFields = [
                ...categoryFields[0].columns,
                ...categoryFields[1].columns,
                ...categoryFields[2].columns,
                ...categoryFields[3].columns,
            ];
            let fieldsWithHoKey = allFields.filter(
                (field: Field) => field.hokey
            );
            return fieldsWithHoKey;
        }

        cancel(): void {
            this.dialog.cancel();
        }

        setColumnWithField(field: Field): void {
            const isSetting = this.column.columnid != field.columnid;
            angular.extend(this.column, this.defaultColumn);
            if (isSetting) {
                this.setPreviousFieldColumnToDefault(field);
                this.column.columnid = field.columnid;
                this.column.columnname = field.columnname;
            }
            this.dialog.hide(this.column);
        }

        setColumnWithElection(election: any): void {
            const isSetting = this.column.elid != election.elid;
            angular.extend(this.column, this.defaultColumn);
            if (isSetting) {
                this.setPreviousFieldColumnToDefault(election);
                this.column.elid = election.elid;
                this.column.elname = election.elname;
            }
            this.dialog.hide(this.column);
        }

        setColumnWithCustomField(customfield: Field): void {
            const isSetting = this.column.cfid != customfield.cfid;
            angular.extend(this.column, this.defaultColumn);
            if (isSetting) {
                this.setPreviousFieldColumnToDefault(customfield);
                this.column.cfid = customfield.cfid;
                this.column.cfname = customfield.name;
                this.column.columnname = customfield.name;
            }
            this.dialog.hide(this.column);
        }

        setPreviousFieldColumnToDefault(field: Field): void {
            let previousColumn = this.columnWithField(field);
            if (previousColumn) {
                angular.extend(previousColumn, this.defaultColumn);
            }
        }

        getFieldKey(field: Field): string | null {
            if (!angular.isUndefined(field.columnid) && field.columnid != null)
                return "columnid";
            else if (!angular.isUndefined(field.elid) && field.elid != null)
                return "elid";
            else if (!angular.isUndefined(field.cfid) && field.cfid != null)
                return "cfid";
            return null;
        }

        currentSelectedField(field: Field): boolean {
            return this.column.columnid === field.columnid;
        }

        currentSelectedElection(election: any): boolean {
            return this.column.elid === election.elid;
        }

        currentSelectedCustomField(customfield: Field): boolean {
            return this.column.cfid === customfield.cfid;
        }

        columnWithField(field: Field): any {
            const searchKey = this.getFieldKey(field);
            if (searchKey) {
                let columns = this.registerColumns.filter((obj: any) => {
                    return obj[searchKey] == field[searchKey];
                });
                if (columns.length > 0) return columns[0];
            }
            return null;
        }

        openDeleteElectionModal(election: any, ev: any): void {
            this.dialog
                .delete({
                    fullscreen: true,
                    targetEvent: ev,
                    skipHide: true,
                    locals: {
                        bodyText:
                            "All voting records associated with this election will be permanently deleted",
                        title: `Delete Election?`,
                    },
                })
                .then(() => {
                    this.electionDataProvider.removeElection(election).then(
                        () => {
                            this.setPreviousFieldColumnToDefault(election);
                            this.toast.success("Election deleted");
                        },
                        () => {
                            this.toast.error();
                        }
                    );
                });
        }

        openFieldDialog(ev: any, field: Field): void {
            if (!field && this.columnname) {
                field = new Field({ name: this.columnname });
            }
            this.dialog
                .show({
                    component: "vc-custom-field-form",
                    targetEvent: ev,
                    scope: this.$scope,
                    size: "large",
                    locals: {
                        bindedField: field,
                        mode: "import",
                    },
                })
                .then((editedField: Field) => {
                    if (angular.isDefined(field.cfid)) {
                        angular.extend(field, editedField);
                    } else {
                        this.setColumnWithCustomField(editedField);
                    }
                });
        }

        deleteField(field: Field, ev: any): void {
            this.dialog
                .delete({
                    fullscreen: true,
                    targetEvent: ev,
                    skipHide: true,
                    locals: {
                        bodyText: "They will be permanently deleted",
                        title: `Delete Custom Field?`,
                    },
                })
                .then(() => {
                    this.fieldDataProvider.deleteField(field).then(
                        () => {
                            this.setPreviousFieldColumnToDefault(field);
                            this.toast.success("Field deleted");
                        },
                        () => {
                            this.toast.error();
                        }
                    );
                });
        }
        disableImportColumn(category: string): boolean {
            if (this.importDataType == "houses") {
                return ["Info", "Personal", "Contact"].includes(category);
            } else if (this.importDataType == "contacts") {
                return category == "Address";
            }
            return false;
        }
    },
};

angular
    .module("voterFile")
    .component(FieldPickerComponent.selector, FieldPickerComponent);
